import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Search,
    X,
    Receipt,
    Loader2,
    Calendar,
    CheckCircle,
    Clock,
    AlertTriangle,
    XCircle,
    DollarSign,
    FileText,
    Barcode,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { adminService, Fatura } from "../../services/adminService";
import { documentService } from "../../services/documentService";
import { formatDateFromDB } from "../../utils/dateUtils";
import { formatInvoiceNumber, parseInvoiceNumber } from "../../utils/formatters";

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
    rascunho: { label: "Rascunho", color: "bg-gray-500/20 text-gray-400", icon: Clock },
    emitida: { label: "Emitida", color: "bg-blue-500/20 text-blue-400", icon: Receipt },
    enviada: { label: "Enviada", color: "bg-yellow-500/20 text-yellow-400", icon: Clock },
    paga: { label: "Paga", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
    cancelada: { label: "Cancelada", color: "bg-red-500/20 text-red-400", icon: XCircle },
    vencida: { label: "Vencida", color: "bg-orange-500/20 text-orange-400", icon: AlertTriangle },
};

export function Faturas() {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const [faturas, setFaturas] = useState<Fatura[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        if (!isAuthenticated) navigate("/area-cliente");
    }, [isAuthenticated, navigate]);

    const clientId = user?.clientId || (user?.role === "client" ? user?.id : undefined);

    useEffect(() => {
        if (clientId) loadFaturas();
    }, [clientId, statusFilter, currentPage]);

    const loadFaturas = async () => {
        if (!clientId) return;
        setIsLoading(true);
        try {
            const result = await adminService.listFaturas({
                clientId,
                status: statusFilter || undefined,
                page: currentPage,
            });
            // clientes só veem faturas visíveis no portal
            const visiveis = result.data.filter((f) => f.visibleInPortal === true || (f.visibleInPortal as any) === 1);
            setFaturas(visiveis);
            setTotalPages(result.pagination?.totalPages || 1);
        } catch (error) {
            console.error("Erro ao carregar faturas:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatCurrency = (value: number) =>
        new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

    const formatDate = (date: string) => formatDateFromDB(date);

    const clearFilters = () => { setSearchTerm(""); setStatusFilter(""); setCurrentPage(1); };

    const filteredFaturas = faturas.filter((f) => {
        // Proteção de busca: permite buscar tanto por "FAT-0029" quanto por "29"
        const searchNumber = parseInvoiceNumber(searchTerm);
        const matchesNumber = searchTerm === "" || 
            String(f.numero).includes(searchTerm) || 
            f.numero === searchNumber ||
            formatInvoiceNumber(f.numero).toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesClient = searchTerm === "" ||
            (f.client?.razaoSocial || "").toLowerCase().includes(searchTerm.toLowerCase());
        
        return matchesNumber || matchesClient;
    });

    const isVencida = (f: Fatura) =>
        ["emitida", "enviada"].includes(f.status) && new Date(f.dataVencimento) < new Date();

    return (
        <>
            <Helmet>
                <title>Faturas | Portal do Cliente</title>
            </Helmet>

            <div className="min-h-screen">
                <div className="py-8">
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-8"
                    >
                        <Link
                            to="/portal"
                            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors mb-4"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Voltar ao Portal
                        </Link>
                        <h1 className="text-3xl md:text-4xl font-bold">
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
                                Faturas
                            </span>
                        </h1>
                        <p className="text-gray-500 dark:text-gray-400 mt-1">Acompanhe suas faturas e histórico de pagamentos</p>
                    </motion.div>

                    {/* Filtros */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="flex flex-col md:flex-row gap-4 mb-6 p-4 rounded-xl card"
                    >
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar faturas..."
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                className="w-full pl-10 pr-4 py-2 rounded-lg bg-background border border-input text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-accent focus:border-accent transition-colors"
                            />
                        </div>
                        <div className="flex flex-wrap gap-4 items-center">
                            <select
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                                className="px-4 py-2 rounded-lg bg-background border border-input text-foreground focus:ring-2 focus:ring-accent focus:border-accent transition-colors"
                            >
                                <option value="">Todos os status</option>
                                <option value="emitida">Emitida</option>
                                <option value="enviada">Enviada</option>
                                <option value="paga">Paga</option>
                                <option value="vencida">Vencida</option>
                            </select>
                            {(searchTerm || statusFilter) && (
                                <button
                                    onClick={clearFilters}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                    Limpar
                                </button>
                            )}
                        </div>
                    </motion.div>

                    {/* Tabela */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="rounded-xl card overflow-hidden"
                    >
                        {isLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                            </div>
                        ) : filteredFaturas.length === 0 ? (
                            <div className="text-center py-12">
                                <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 dark:text-gray-400">Nenhuma fatura encontrada</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-muted/50 border-b border-border">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Nº</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400 hidden md:table-cell">Emissão</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Vencimento</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Valor</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Status</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400 hidden lg:table-cell">Documentos</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {filteredFaturas.map((fatura) => {
                                            const st = statusConfig[fatura.status] ?? statusConfig.emitida;
                                            const StatusIcon = st.icon;
                                            const vencida = isVencida(fatura);
                                            return (
                                                <tr key={fatura.id} className="hover:bg-muted/50 transition-colors">
                                                    <td className="px-4 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <DollarSign className="w-5 h-5 text-teal-500 flex-shrink-0" />
                                                            <span className="text-sm text-gray-900 dark:text-gray-100">{formatInvoiceNumber(fatura.numero)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4 hidden md:table-cell">
                                                        <span className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                                                            <Calendar className="w-3 h-3" />
                                                            {formatDate(fatura.dataEmissao)}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <span className={`inline-flex items-center gap-1 text-sm ${vencida ? "text-orange-500 font-medium" : "text-gray-500 dark:text-gray-400"}`}>
                                                            <Calendar className="w-3 h-3" />
                                                            {formatDate(fatura.dataVencimento)}
                                                            {vencida && <AlertTriangle className="w-3 h-3 ml-1" />}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                            {formatCurrency(Number(fatura.valorTotal))}
                                                        </span>
                                                        {fatura.status === "paga" && fatura.valorPago && (
                                                            <p className="text-xs text-gray-400 mt-0.5">
                                                                Pago: {formatCurrency(Number(fatura.valorPago))}
                                                            </p>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${st.color}`}>
                                                            <StatusIcon className="w-3 h-3" />
                                                            {st.label}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4 hidden lg:table-cell">
                                                        {fatura.documentos && fatura.documentos.length > 0 ? (
                                                            <div className="flex items-center gap-2">
                                                                {fatura.documentos.map((doc) => {
                                                                    const isNF = doc.tipoDocumento?.isNf || doc.tipoDocumento.nome.toLowerCase().includes('nota');
                                                                    const isBoleto = doc.tipoDocumento.nome.toLowerCase().includes('boleto');
                                                                    return (
                                                                        <button
                                                                            key={doc.id}
                                                                            onClick={() => documentService.downloadFile(doc.id, doc.arquivo)}
                                                                            className="p-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                                                                            title={`Download ${doc.nome}`}
                                                                        >
                                                                            {isNF ? (
                                                                                <FileText className="w-4 h-4 text-teal-500" />
                                                                            ) : isBoleto ? (
                                                                                <Barcode className="w-4 h-4 text-blue-500" />
                                                                            ) : (
                                                                                <FileText className="w-4 h-4 text-gray-400" />
                                                                            )}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-400 text-xs">—</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <button
                                                            onClick={() => navigate(`/portal/faturas/${fatura.id}`)}
                                                            className="px-3 py-1 rounded-lg bg-teal-50 text-gray-600 hover:bg-teal-100 dark:bg-teal-500/10 dark:text-teal-400 dark:hover:bg-teal-500/20 transition-colors text-sm"
                                                        >
                                                            Ver Detalhes
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Paginação */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 p-4 border-t border-gray-200 dark:border-coddar-border">
                                <button
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Anterior
                                </button>
                                <span className="text-sm text-gray-500 dark:text-gray-400">Página {currentPage} de {totalPages}</span>
                                <button
                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Próxima
                                </button>
                            </div>
                        )}
                    </motion.div>
                </div>
            </div>
        </>
    );
}
